<template>
    <div class="top-tools-comp">
        <div class="full-navbar" :class="{ active: layout === 'full' }">
            <nav>
                <ul>
                    <li>
                        <router-link to="/home">
                            <House />
                        </router-link>
                    </li>
                    <li>
                        <router-link to="/search">
                            <Search />
                        </router-link>
                    </li>
                    <li>
                        <router-link to="/add">
                            <Plus />
                        </router-link>
                    </li>
                    <li>
                        <router-link to="/tasks">
                            <CircleCheckBig />
                        </router-link>
                    </li>
                    <li>
                        <button class="menu-nav-btn" @click="toggleMenuModal">
                            <Ellipsis />
                        </button>
                    </li>
                </ul>
            </nav>
        </div>
        <div class="searchbar" :class="{ active: layout === 'search' }">
            <div class="searchbar-content">
                <span>
                    <Search />
                </span>
                <input type="text" placeholder="Busque por avaliações...">
            </div>
            <ul>
                <li>
                    <router-link to="/settings">
                        <Plus />
                    </router-link>
                </li>
            </ul>
        </div>
        <div class="split-navbar" :class="{ active: layout === 'split' }">
            <nav>
                <ul>
                    <li>
                        <router-link to="/settings">
                            <img src="@/assets/logo_template_vertical.svg" alt="logo">
                        </router-link>
                    </li>
                </ul>
                <ul>
                    <li class="desktop-only">
                        <router-link :to="dashboardLink">
                            <House />
                            <span class="nav-label">Dashboard</span>
                        </router-link>
                    </li>
                    <li class="desktop-only">
                        <router-link :to="shopLink">
                            <Store />
                            <span class="nav-label">Shop</span>
                        </router-link>
                    </li>
                    <li class="desktop-only">
                        <router-link :to="studentsLink">
                            <Users />
                            <span class="nav-label">{{ isSchool ? 'Responsáveis' : 'Alunos' }}</span>
                        </router-link>
                    </li>
                    <li class="desktop-only">
                        <router-link :to="settingsLink">
                            <CircleDollarSign v-if="isSchool" />
                            <Puzzle v-else />
                            <span class="nav-label">{{ isSchool ? 'Financeiro' : 'Ferramentas' }}</span>
                        </router-link>
                    </li>
                    <li>
                        <button class="menu-nav-btn bell-btn" disabled style="opacity: 0.4; cursor: not-allowed; pointer-events: none;">
                            <Bell />
                            <span v-if="hasUnreadNotifications" class="bell-unread-dot"></span>
                        </button>
                    </li>
                    <li v-if="showCart">
                        <router-link to="/clients/cart" class="cart-highlight">
                            <ShoppingCart :size="20" />
                            <span v-if="cartCount > 0" class="cart-badge">{{ cartCount }}</span>
                        </router-link>
                    </li>
                    <li>
                        <button class="menu-nav-btn" @click="toggleMenuModal">
                            <Ellipsis />
                        </button>
                    </li>
                </ul>
            </nav>
        </div>

        <!-- Menu Modal (Laranja) -->
        <Transition name="menu-fade">
            <div v-if="isMenuOpen" class="menu-modal-backdrop" @click="closeMenuModal">
                <Transition name="menu-scale">
                    <div class="menu-modal-card" @click.stop>
                        <!-- Botão de Fechar -->
                        <button class="close-menu-btn" @click="closeMenuModal">
                            <X :size="20" />
                        </button>

                        <!-- Seção de Perfil -->
                        <div class="menu-profile-section">
                            <h3 class="profile-name">{{ profileName }}</h3>
                            <span class="profile-role">{{ profileRole }}</span>
                        </div>

                        <!-- Estudantes Vinculados -->
                        <div class="menu-students-section" v-if="!isSchool && clientStudents && clientStudents.length > 0">
                            <h4 class="students-title">Estudantes Vinculados</h4>
                            <div class="students-list">
                                <div v-for="student in clientStudents" :key="student.id" class="student-item">
                                    <div class="student-avatar">{{ getInitials(student.name) }}</div>
                                    <div class="student-info">
                                        <span class="student-name">{{ student.name }}</span>
                                        <span class="student-grade">{{ student.grade }} - {{ student.shift }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Botão de Sair -->
                        <button class="menu-logout-btn" @click="handleLogout">
                            <LogOut :size="18" />
                            <span>Sair da Conta</span>
                        </button>
                    </div>
                </Transition>
            </div>
        </Transition>

        <!-- Modal Obrigatório de Alteração de Senha Inicial -->
        <div v-if="mustChangePassword" class="password-force-backdrop">
            <div class="password-force-card">
                <div class="lock-icon-container">
                    <Lock :size="32" />
                </div>
                <h2>Nova Senha de Acesso</h2>
                <p>Para sua segurança, você deve alterar a senha inicial gerada pela escola antes de prosseguir.</p>
                
                <form @submit.prevent="forceChangePassword" class="password-force-form">
                    <div class="input-wrapper">
                        <input 
                            v-model="newPassword" 
                            :type="showPass ? 'text' : 'password'" 
                            placeholder="Nova Senha (mín. 6 caracteres)" 
                            required
                        >
                    </div>
                    <div class="input-wrapper">
                        <input 
                            v-model="confirmPassword" 
                            :type="showPass ? 'text' : 'password'" 
                            placeholder="Confirme a Nova Senha" 
                            required
                        >
                    </div>
                    
                    <div class="show-pass-checkbox">
                        <label>
                            <input type="checkbox" v-model="showPass"> Mostrar senha
                        </label>
                    </div>
                    
                    <button type="submit" :disabled="isChangingPassword">
                        {{ isChangingPassword ? 'Salvando...' : 'Definir Nova Senha' }}
                    </button>
                </form>
            </div>
        </div>

        <!-- Modal de Notificações (Laranja) -->
        <Transition name="menu-fade">
            <div v-if="isNotificationsOpen" class="menu-modal-backdrop" @click="closeNotificationsModal">
                <Transition name="menu-scale">
                    <div class="menu-modal-card notifications-card" @click.stop>
                        <!-- Botão de Fechar -->
                        <button class="close-menu-btn" @click="closeNotificationsModal">
                            <X :size="20" />
                        </button>

                        <!-- Visão de Detalhes da Notificação -->
                        <div v-if="selectedNotification" class="notification-detail-view">
                            <button class="notif-back-btn" @click="selectedNotification = null">
                                <ChevronLeft :size="16" />
                                <span>Voltar</span>
                            </button>
                            <div class="detail-header">
                                <span class="detail-time">{{ selectedNotification.time }}</span>
                                <h3 class="detail-title">{{ selectedNotification.title }}</h3>
                            </div>
                            <p class="detail-body">{{ selectedNotification.message }}</p>
                        </div>

                        <!-- Visão de Lista de Notificações -->
                        <div v-else class="notification-list-view">
                            <h3 class="notifications-title">Notificações</h3>
                            <div class="notifications-list-container">
                                <div 
                                    v-for="notif in notifications" 
                                    :key="notif.id" 
                                    class="notification-item"
                                    :class="{ unread: !notif.read }"
                                    @click="readNotification(notif)"
                                >
                                    <div class="notif-badge-dot" v-if="!notif.read"></div>
                                    <div class="notif-content">
                                        <span class="notif-title">{{ notif.title }}</span>
                                        <span class="notif-preview">{{ notif.message }}</span>
                                        <span class="notif-time">{{ notif.time }}</span>
                                    </div>
                                    <ChevronRight :size="16" class="notif-arrow" />
                                </div>
                            </div>
                        </div>
                    </div>
                </Transition>
            </div>
        </Transition>
    </div>
</template>

<script>
import { Lock, House, Store, Users, Puzzle, Search, Plus, CircleCheckBig, Ellipsis, Bell, ShoppingCart, X, LogOut, ChevronLeft, ChevronRight, CircleDollarSign } from 'lucide-vue-next';

export default {
    name: 'TopToolComp',
    props: {
        layout: {
            type: String,
            default: 'split',
            validator: (value) => ['full', 'search', 'split'].includes(value)
        }
    },
    components: {
        Lock,
        House,
        Store,
        Users,
        Puzzle,
        Search,
        Plus,
        CircleCheckBig,
        Ellipsis,
        Bell,
        ShoppingCart,
        X,
        LogOut,
        ChevronLeft,
        ChevronRight,
        CircleDollarSign
    },
    data() {
        return {
            isMenuOpen: false,
            isNotificationsOpen: false,
            selectedNotification: null,
            clientUser: null,
            clientStudents: [],
            newPassword: '',
            confirmPassword: '',
            showPass: false,
            isChangingPassword: false,
            notifications: [
                {
                    id: 1,
                    title: 'Pedido Pronto para Retirada',
                    message: 'O uniforme oficial tamanho P do aluno Lucas Silva foi entregue na secretaria e já está disponível para retirada na secretaria da escola.',
                    time: 'Hoje, 10:30',
                    read: false
                },
                {
                    id: 2,
                    title: 'Prazo Limite de Compra',
                    message: 'Atenção pais e responsáveis: o prazo limite de compras de ingressos para a Festa Junina se encerra neste domingo. Não perca!',
                    time: 'Ontem, 16:15',
                    read: false
                },
                {
                    id: 3,
                    title: 'Confirmação de Pagamento',
                    message: 'O pagamento referente à fatura de uniformes escolares de maio foi confirmado com sucesso pelo Mercado Pago.',
                    time: '14 de maio',
                    read: true
                }
            ]
        };
    },
    mounted() {
        this.loadUserInfo();
    },
    computed: {
        showCart() {
            const path = this.$route.path;
            return path.startsWith('/clients') && path !== '/clients/login';
        },
        cartCount() {
            return this.$store.state.cartCount;
        },
        hasUnreadNotifications() {
            return this.notifications.some(n => !n.read);
        },
        isSchool() {
            return this.$route.path.startsWith('/school/');
        },
        dashboardLink() {
            return this.isSchool ? '/school/dashboard' : '/clients/dashboard';
        },
        shopLink() {
            return this.isSchool ? '/school/shop' : '/clients/shop';
        },
        studentsLink() {
            return this.isSchool ? '/school/clients' : '/clients/students';
        },
        settingsLink() {
            return this.isSchool ? '/school/finance' : '/clients/settings';
        },
        mustChangePassword() {
            const user = this.clientUser;
            if (!user || user.role !== 'client') return false;
            if (this.$route.path === '/clients/login') return false;
            
            return !!user.must_change_password;
        },
        profileName() {
            if (this.isSchool) return 'Secretaria Escolar';
            return this.clientUser ? this.clientUser.name : 'Responsável';
        },
        profileRole() {
            return this.isSchool ? 'Administrador da Escola' : 'Responsável Financeiro';
        },

    },
    methods: {
        async loadUserInfo() {
            try {
                const storedUser = localStorage.getItem('user');
                if (storedUser) {
                    this.clientUser = JSON.parse(storedUser);
                    
                    // Auto-limpeza de flag de senha incorreta para não-clientes
                    if (this.clientUser && this.clientUser.role !== 'client' && this.clientUser.must_change_password) {
                        delete this.clientUser.must_change_password;
                        localStorage.setItem('user', JSON.stringify(this.clientUser));
                    }
                    
                    // Se o usuário precisa alterar a senha inicial, retorna imediatamente sem fazer chamadas de API
                    if (this.clientUser && this.clientUser.must_change_password) {
                        return;
                    }
                    
                    // Se for cliente, busca seus alunos/filhos associados
                    if (this.clientUser && this.clientUser.role === 'client' && this.clientUser.id) {
                        const { apiRequest } = await import('@/services/api');
                        const data = await apiRequest(`/clients/shop-data?client_id=${this.clientUser.id}`);
                        if (data && data.status === 'success' && data.students) {
                            this.clientStudents = data.students;
                        }
                    }
                }
            } catch (err) {
                console.error('Erro ao carregar dados do usuário no menu:', err);
            }
        },
        getInitials(name) {
            if (!name) return 'A';
            const parts = name.split(' ');
            if (parts.length > 1) {
                return (parts[0][0] + parts[1][0]).toUpperCase();
            }
            return parts[0][0].toUpperCase();
        },
        toggleMenuModal() {
            this.isMenuOpen = !this.isMenuOpen;
            this.isNotificationsOpen = false;
            if (this.isMenuOpen) {
                this.loadUserInfo();
            }
        },
        closeMenuModal() {
            this.isMenuOpen = false;
        },
        toggleNotificationsModal() {
            this.isNotificationsOpen = !this.isNotificationsOpen;
            this.isMenuOpen = false;
            this.selectedNotification = null;
        },
        closeNotificationsModal() {
            this.isNotificationsOpen = false;
            this.selectedNotification = null;
        },
        readNotification(notif) {
            notif.read = true;
            this.selectedNotification = notif;
        },
        async forceChangePassword() {
            if (this.newPassword.length < 6) {
                this.$store.dispatch('showToast', {
                    title: 'Senha muito curta',
                    message: 'A nova senha deve ter pelo menos 6 caracteres.',
                    type: 'warning'
                });
                return;
            }
            if (this.newPassword !== this.confirmPassword) {
                this.$store.dispatch('showToast', {
                    title: 'Senhas diferentes',
                    message: 'A confirmação de senha não coincide com a nova senha.',
                    type: 'warning'
                });
                return;
            }

            this.isChangingPassword = true;
            try {
                const { apiRequest } = await import('@/services/api');
                const data = await apiRequest('/clients/change-password', {
                    method: 'POST',
                    body: {
                        new_password: this.newPassword
                    }
                });

                if (data && data.status === 'success') {
                    const storedUser = localStorage.getItem('user');
                    if (storedUser) {
                        const user = JSON.parse(storedUser);
                        user.must_change_password = false;
                        localStorage.setItem('user', JSON.stringify(user));
                        this.clientUser = user;
                    }

                    this.$store.dispatch('showToast', {
                        title: 'Senha Atualizada!',
                        message: 'Sua nova senha foi salva. Bem-vindo ao portal!',
                        type: 'success'
                    });

                    this.newPassword = '';
                    this.confirmPassword = '';
                }
            } catch (err) {
                console.error(err);
            } finally {
                this.isChangingPassword = false;
            }
        },
        handleLogout() {
            this.closeMenuModal();
            localStorage.removeItem('user');
            this.clientUser = null;
            this.clientStudents = [];
            this.$store.dispatch('showToast', {
                title: 'Até logo!',
                message: 'Você saiu da sua conta com sucesso.',
                type: 'success',
                duration: 3000
            });
            if (this.isSchool) {
                this.$router.push('/school/login');
            } else {
                this.$router.push('/clients/login');
            }
        }
    }
}
</script>

<style lang="less" scoped>
.top-tools-comp {
    position: fixed;
    left: 0;
    right: 0;
    margin-left: auto;
    margin-right: auto;
    top: 0px;
    width: 100%;
    z-index: 999999;
    padding: 24px;
    padding-top: max(24px, env(safe-area-inset-top));
    display: flex;
    align-items: center;
    justify-content: center;

    @media (min-width: 1024px) {
        max-width: 1200px;
        padding-top: 24px;
    }

    &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        backdrop-filter: blur(15px);
        -webkit-backdrop-filter: blur(15px);
        mask-image: linear-gradient(to bottom, black 0%, transparent 100%);
        -webkit-mask-image: linear-gradient(to bottom, black 0%, transparent 100%);
        z-index: -1;
        pointer-events: none;
    }

    .full-navbar {
        display: none;
        width: 100%;

        nav {
            width: 100%;

            ul {
                width: 100%;
                list-style: none;
                display: flex;
                align-items: center;
                justify-content: space-between;
                // background-image: radial-gradient(#009eff, #0093ef);
                background-color: var(--orange);
                border-radius: 100px;
                backdrop-filter: blur(10px);
                -webkit-backdrop-filter: blur(10px);
                box-shadow: -.5px -.5px .5px 0px #ffffff33, .5px .5px .5px 0px #ffffff22;

                li {
                    padding: 6px;

                    a {
                        width: 43px;
                        height: 43px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 50%;
                        color: #ffffff;
                    }
                }
            }
        }
    }

    .split-navbar {
        display: none;
        width: 100%;

        nav {
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 12px;

            ul {
                width: 100%;
                list-style: none;
                display: flex;
                align-items: center;
                justify-content: space-between;
                // background-image: radial-gradient(#009eff, #0093ef);
                background-color: var(--orange);
                border-radius: 100px;
                backdrop-filter: blur(10px);
                -webkit-backdrop-filter: blur(10px);
                box-shadow: -.5px -.5px .5px 0px #ffffff33, .5px .5px .5px 0px #ffffff22;

                &:first-child {
                    width: 55px;
                    height: 55px;
                    display: flex;
                    align-items: center;
                    justify-content: flex-start;
                    border-radius: 50%;
                    background: none;
                    box-shadow: none;
                    backdrop-filter: blur(0px);

                    li {
                        padding: 0;
                        display: flex;
                        align-items: center;
                        justify-content: center;

                        a {
                            width: 100%;
                            height: 100%;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            border-radius: 50%;
                            padding: 0;
                            min-width: unset;

                            img {
                                width: 40px;
                                height: auto;
                            }
                        }
                    }
                }

                &:last-child {
                    width: min-content;
                }

                li {
                    padding: 6px;

                    &.desktop-only {
                        display: none;

                        @media (min-width: 1024px) {
                            display: block;
                        }
                    }

                    a {
                        width: auto;
                        min-width: 43px;
                        height: 43px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 100px;
                        color: #ffffff;
                        padding: 0 10px;
                        gap: 8px;
                        text-decoration: none;

                        .nav-label {
                            display: none;

                            @media (min-width: 1024px) {
                                display: block;
                                font-size: 14px;
                                font-weight: 500;
                                padding-right: 5px;
                            }
                        }
                    }
                }
            }
        }
    }

    .searchbar {
        display: none;
        width: 100%;
        justify-content: space-between;
        align-items: center;
        gap: 12px;

        .searchbar-content {
            padding: 6px;
            width: 100%;
            display: flex;
            justify-content: flex-start;
            align-items: center;
            gap: 6px;
            // background-image: radial-gradient(#009eff, #0093ef);
            background-color: var(--orange);
            border-radius: 100px;
            backdrop-filter: blur(10px);
            -webkit-backdrop-filter: blur(10px);
            box-shadow: -.5px -.5px .5px 0px #ffffff33, .5px .5px .5px 0px #ffffff22;

            span {
                width: 43px;
                height: 43px;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 100px;
                color: #ffffff;

            }

            input {
                background: transparent;
                border: none;
                outline: none;
                color: #ffffff;
                width: calc(100% - 43px);
                height: 43px;
                font-size: 16px;
                font-weight: light;

                &::placeholder {
                    color: #ffffff44;
                }
            }
        }

        ul {
            width: min-content;
            list-style: none;
            display: flex;
            align-items: center;
            justify-content: space-between;
            // background-image: radial-gradient(#009eff, #0093ef);
            background-color: var(--orange);
            border-radius: 100px;
            backdrop-filter: blur(10px);
            -webkit-backdrop-filter: blur(10px);
            box-shadow: -.5px -.5px .5px 0px #ffffff33, .5px .5px .5px 0px #ffffff22;

            li {
                padding: 6px;

                a {
                    width: 43px;
                    height: 43px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 50%;
                    color: #ffffff;
                }

                .active-link {
                    background-color: var(--dark-04);
                }
            }
        }
    }

    .active {
        display: flex;
    }

    .active-link,
    .router-link-active {
        background-color: var(--yellow) !important;
        color: #005d97 !important;
    }

    .cart-highlight {
        background-color: var(--green) !important;
        color: var(--white) !important;
        position: relative;
        box-shadow: 0 4px 12px rgba(36, 189, 46, 0.4);

        .cart-badge {
            position: absolute;
            top: -5px;
            right: -5px;
            min-width: 18px;
            height: 18px;
            padding: 0 4px;
            background-color: #ff3b30;
            color: white;
            border-radius: 10px;
            font-size: 10px;
            font-weight: 800;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 2px solid white;
            box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
        }
    }

    .menu-nav-btn {
        width: 43px;
        height: 43px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        color: #ffffff;
        background: transparent;
        border: none;
        cursor: pointer;
        padding: 0;
        transition: all 0.2s ease;

        &:hover {
            background-color: rgba(255, 255, 255, 0.15);
        }

        &:active {
            transform: scale(0.9);
        }
    }

    /* MENU MODAL (LARANJA) STYLES */
    .menu-modal-backdrop {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: rgba(0, 0, 0, 0.65);
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        z-index: 9999999;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;

        .menu-modal-card {
            background: var(--orange);
            width: 100%;
            max-width: 400px;
            border-radius: 32px;
            padding: 30px 24px 24px 24px;
            position: relative;
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.35);
            border: 1px solid rgba(255, 255, 255, 0.25);
            text-align: center;

            .close-menu-btn {
                position: absolute;
                top: 18px;
                right: 18px;
                width: 34px;
                height: 34px;
                border-radius: 50%;
                background: rgba(255, 255, 255, 0.15);
                border: none;
                color: #ffffff;
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                transition: all 0.2s ease;
                z-index: 10;

                &:active {
                    transform: scale(0.9);
                }
            }

            .menu-profile-section {
                display: flex;
                flex-direction: column;
                align-items: center;
                margin-bottom: 24px;

                .profile-name {
                    font-size: 20px;
                    font-weight: 800;
                    color: #ffffff;
                    margin: 0 0 4px 0;
                    text-align: center;
                }

                .profile-role {
                    font-size: 12px;
                    font-weight: 500;
                    color: rgba(255, 255, 255, 0.8);
                }
            }

            .menu-students-section {
                width: 100%;
                background: rgba(255, 255, 255, 0.1);
                border: 1px solid rgba(255, 255, 255, 0.15);
                border-radius: 24px;
                padding: 16px;
                margin-bottom: 24px;
                text-align: left;

                .students-title {
                    margin: 0 0 12px 0;
                    font-size: 11px;
                    font-weight: 700;
                    color: #ffffff;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    opacity: 0.9;
                }

                .students-list {
                    display: flex;
                    flex-direction: column;
                    gap: 10px;

                    .student-item {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                        padding: 8px;
                        border-radius: 16px;
                        background: rgba(255, 255, 255, 0.05);

                        .student-avatar {
                            width: 36px;
                            height: 36px;
                            border-radius: 10px;
                            background: rgba(255, 255, 255, 0.2);
                            color: #ffffff;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            font-weight: 800;
                            font-size: 13px;
                            flex-shrink: 0;
                        }

                        .student-info {
                            display: flex;
                            flex-direction: column;

                            .student-name {
                                font-size: 13px;
                                font-weight: 700;
                                color: #ffffff;
                                line-height: 1.2;
                            }

                            .student-grade {
                                font-size: 10px;
                                color: rgba(255, 255, 255, 0.8);
                                margin-top: 1px;
                            }
                        }
                    }
                }
            }

            .menu-logout-btn {
                width: 100%;
                background: #ffffff;
                color: var(--orange);
                border: none;
                padding: 14px;
                border-radius: 18px;
                font-size: 14px;
                font-weight: 700;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                cursor: pointer;
                box-shadow: 0 6px 15px rgba(255, 122, 0, 0.2);
                transition: all 0.2s ease;

                &:hover {
                    background: rgba(255, 255, 255, 0.95);
                }

                &:active {
                    transform: scale(0.97);
                }
            }
        }
    }

    /* TRANSITIONS */
    .menu-fade-enter-active, .menu-fade-leave-active {
        transition: opacity 0.3s ease;
    }
    .menu-fade-enter-from, .menu-fade-leave-to {
        opacity: 0;
    }

    .menu-scale-enter-active, .menu-scale-leave-active {
        transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    }
    .menu-scale-enter-from, .menu-scale-leave-to {
        transform: scale(0.9);
    }

    .bell-btn {
        position: relative;

        .bell-unread-dot {
            position: absolute;
            top: 8px;
            right: 8px;
            width: 8px;
            height: 8px;
            background-color: var(--yellow);
            border-radius: 50%;
            border: 1.5px solid var(--orange);
            box-shadow: 0 0 6px rgba(255, 255, 255, 0.4);
        }
    }

    .notifications-card {
        max-width: 420px !important;

        .notifications-title {
            margin: 0 0 16px 0;
            font-size: 18px;
            font-weight: 800;
            color: #ffffff;
            text-align: left;
        }

        .notifications-list-container {
            display: flex;
            flex-direction: column;
            gap: 10px;
            max-height: 320px;
            overflow-y: auto;
            padding-right: 4px;

            /* Custom Scrollbar */
            &::-webkit-scrollbar {
                width: 6px;
            }
            &::-webkit-scrollbar-track {
                background: rgba(255, 255, 255, 0.05);
                border-radius: 10px;
            }
            &::-webkit-scrollbar-thumb {
                background: rgba(255, 255, 255, 0.2);
                border-radius: 10px;
            }

            .notification-item {
                display: flex;
                align-items: center;
                gap: 12px;
                padding: 14px 16px;
                border-radius: 20px;
                background: rgba(255, 255, 255, 0.07);
                border: 1px solid rgba(255, 255, 255, 0.1);
                cursor: pointer;
                transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
                position: relative;

                &:hover {
                    background: rgba(255, 255, 255, 0.13);
                    border-color: rgba(255, 255, 255, 0.2);
                    transform: translateY(-1px);
                }

                &:active {
                    transform: translateY(0);
                }

                &.unread {
                    background: rgba(255, 255, 255, 0.18);
                    border-color: rgba(255, 255, 255, 0.3);
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
                }

                .notif-badge-dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    background: var(--yellow);
                    flex-shrink: 0;
                    box-shadow: 0 0 6px var(--yellow);
                }

                .notif-content {
                    display: flex;
                    flex-direction: column;
                    gap: 3px;
                    text-align: left;
                    flex: 1;
                    overflow: hidden;

                    .notif-title {
                        font-size: 13.5px;
                        font-weight: 700;
                        color: #ffffff;
                    }

                    .notif-preview {
                        font-size: 11.5px;
                        color: rgba(255, 255, 255, 0.8);
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }

                    .notif-time {
                        font-size: 10px;
                        color: rgba(255, 255, 255, 0.65);
                        margin-top: 1px;
                    }
                }

                .notif-arrow {
                    color: rgba(255, 255, 255, 0.6);
                    flex-shrink: 0;
                    transition: transform 0.2s;
                }

                &:hover .notif-arrow {
                    transform: translateX(2px);
                    color: #ffffff;
                }
            }
        }

        .notification-detail-view {
            text-align: left;
            display: flex;
            flex-direction: column;
            animation: fadeIn 0.3s ease;

            .notif-back-btn {
                background: rgba(255, 255, 255, 0.15);
                border: none;
                color: #ffffff;
                display: inline-flex;
                align-items: center;
                align-self: flex-start;
                gap: 5px;
                font-size: 11px;
                font-weight: 700;
                cursor: pointer;
                margin-bottom: 20px;
                padding: 6px 12px;
                border-radius: 12px;
                transition: all 0.2s;

                &:hover {
                    background: rgba(255, 255, 255, 0.25);
                }
            }

            .detail-header {
                border-bottom: 1px solid rgba(255, 255, 255, 0.15);
                padding-bottom: 12px;
                margin-bottom: 16px;

                .detail-time {
                    font-size: 10px;
                    font-weight: 600;
                    color: rgba(255, 255, 255, 0.7);
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .detail-title {
                    font-size: 17px;
                    font-weight: 800;
                    color: #ffffff;
                    margin: 4px 0 0 0;
                    line-height: 1.3;
                }
            }

            .detail-body {
                font-size: 13px;
                line-height: 1.6;
                color: rgba(255, 255, 255, 0.95);
                margin: 0;
            }
        }
    }

    .password-force-backdrop {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: rgba(15, 23, 42, 0.8);
        backdrop-filter: blur(15px);
        -webkit-backdrop-filter: blur(15px);
        z-index: 99999999;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
    }

    .password-force-card {
        background: #ffffff;
        border-radius: 30px;
        width: 100%;
        max-width: 440px;
        padding: 40px 30px;
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        text-align: center;
        border: 1px solid rgba(255, 255, 255, 0.2);
        animation: scaleUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);

        .lock-icon-container {
            width: 64px;
            height: 64px;
            background: rgba(0, 158, 255, 0.1);
            color: var(--blue);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 24px;
        }

        h2 {
            font-size: 22px;
            font-weight: 700;
            color: #0f172a;
            margin-bottom: 12px;
        }

        p {
            font-size: 14px;
            color: #64748b;
            line-height: 1.5;
            margin-bottom: 30px;
            padding: 0 10px;
        }

        .password-force-form {
            display: flex;
            flex-direction: column;
            gap: 16px;

            .input-wrapper {
                position: relative;
                width: 100%;

                input {
                    width: 100%;
                    padding: 16px 20px;
                    border-radius: 16px;
                    border: 2px solid #e2e8f0;
                    font-size: 14px;
                    font-weight: 500;
                    color: #0f172a;
                    outline: none;
                    transition: all 0.2s;

                    &:focus {
                        border-color: var(--blue);
                        box-shadow: 0 0 0 4px rgba(0, 158, 255, 0.15);
                    }
                }
            }

            .show-pass-checkbox {
                display: flex;
                align-items: center;
                justify-content: flex-start;
                padding-left: 4px;
                margin-top: -4px;

                label {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 13px;
                    color: #64748b;
                    cursor: pointer;
                    user-select: none;
                }
            }

            button {
                width: 100%;
                padding: 16px;
                border-radius: 16px;
                border: none;
                background: var(--blue);
                color: #ffffff;
                font-size: 15px;
                font-weight: 700;
                cursor: pointer;
                transition: all 0.2s;
                box-shadow: 0 10px 15px -3px rgba(0, 158, 255, 0.3);

                &:hover:not(:disabled) {
                    background: #008be0;
                    transform: translateY(-1px);
                    box-shadow: 0 12px 20px -3px rgba(0, 158, 255, 0.4);
                }

                &:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }
            }
        }
    }

    @keyframes scaleUp {
        from {
            opacity: 0;
            transform: scale(0.9);
        }
        to {
            opacity: 1;
            transform: scale(1);
        }
    }
}
</style>
